import { useRef, type ReactNode } from "react";
import { useIsMobile } from "~/hooks/useIsMobile";

const SWIPE_DISTANCE_THRESHOLD = 50;
const SWIPE_RATIO_THRESHOLD = 1.2;
const SWIPE_TIME_THRESHOLD_MS = 600;

interface SwipeableCalendarProps {
  children: ReactNode;
  className?: string;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

export const SwipeableCalendar = ({
  children,
  className,
  onSwipeLeft,
  onSwipeRight,
}: SwipeableCalendarProps) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<{ x: number; y: number; t: number } | null>(null);

  const clickTaakArrow = (direction: "next" | "prev") => {
    // In @taak/react-modern-calendar-datepicker, the suffix refers to arrow
    // icon direction, not position: `.-left` triggers NEXT, `.-right` triggers PREVIOUS.
    const selector =
      direction === "next"
        ? ".Calendar__monthArrowWrapper.-left"
        : ".Calendar__monthArrowWrapper.-right";
    const btn = containerRef.current?.querySelector<HTMLButtonElement>(selector);
    if (btn && !btn.disabled) btn.click();
  };

  const handleSwipe = (direction: "next" | "prev") => {
    if (direction === "next") {
      if (onSwipeLeft) onSwipeLeft();
      else clickTaakArrow("next");
    } else {
      if (onSwipeRight) onSwipeRight();
      else clickTaakArrow("prev");
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const t = e.touches[0];
    if (!t) return;
    startRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !startRef.current) return;
    const end = e.changedTouches[0];
    const start = startRef.current;
    startRef.current = null;
    if (!end) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    const dt = Date.now() - start.t;
    if (dt > SWIPE_TIME_THRESHOLD_MS) return;
    if (Math.abs(dx) < SWIPE_DISTANCE_THRESHOLD) return;
    if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO_THRESHOLD) return;
    handleSwipe(dx < 0 ? "next" : "prev");
  };

  return (
    <div
      ref={containerRef}
      className={className}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={isMobile ? { touchAction: "pan-y" } : undefined}
    >
      {children}
    </div>
  );
};
